import React, { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';

/**
 * ProgressionChart - Visualizes progression data as a line chart
 * Shows accuracy trends over time/questions
 */
const ProgressionChart = ({
  data = [],
  title = 'Learning Progression',
  category = null,
  isLoading = false,
  height = 300
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Calculate chart dimensions
    const padding = { top: 30, right: 20, bottom: 50, left: 60 };
    const chartWidth = 800;
    const chartHeight = height;
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    // Get min/max accuracy for Y-axis scale
    const accuracies = data.map(d => d.accuracy);
    const minAccuracy = Math.min(...accuracies, 0);
    const maxAccuracy = Math.max(...accuracies, 100);
    const yRange = maxAccuracy - minAccuracy || 100;

    // Calculate points for each progression item
    const points = data.map((item, index) => {
      const x = padding.left + (index / (data.length - 1 || 1)) * innerWidth;
      const y = padding.top + innerHeight - ((item.accuracy - minAccuracy) / yRange) * innerHeight;
      return { x, y, ...item };
    });

    return {
      chartWidth,
      chartHeight,
      padding,
      innerWidth,
      innerHeight,
      points,
      minAccuracy,
      maxAccuracy,
      yRange
    };
  }, [data, height]);

  if (isLoading) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex items-center justify-center" style={{ height }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex flex-col items-center justify-center" style={{ height }}>
        <div className="text-gray-400 text-center">
          <p className="mb-2">No progression data yet</p>
          <p className="text-sm text-gray-500">Answer at least 50 questions to see your progression</p>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return null;
  }

  const {
    chartWidth,
    chartHeight,
    padding,
    points,
    minAccuracy,
    maxAccuracy
  } = chartData;

  // Generate path for line
  const pathData = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  // Calculate Y-axis labels
  const yLabels = [];
  for (let i = 0; i <= 4; i++) {
    const value = minAccuracy + (maxAccuracy - minAccuracy) * (i / 4);
    yLabels.push({
      value: Math.round(value),
      y: padding.top + (chartHeight - padding.top - padding.bottom) * (1 - i / 4)
    });
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-cyan-500" />
        <h3 className="text-lg font-semibold text-white">
          {category ? `${category} - ${title}` : title}
        </h3>
      </div>

      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          width={chartWidth}
          height={chartHeight}
          className="mx-auto"
        >
          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={chartHeight - padding.bottom}
            stroke="#4b5563"
            strokeWidth="1"
          />

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={chartHeight - padding.bottom}
            x2={chartWidth - padding.right}
            y2={chartHeight - padding.bottom}
            stroke="#4b5563"
            strokeWidth="1"
          />

          {/* Y-axis grid lines and labels */}
          {yLabels.map((label, i) => (
            <g key={`y-label-${i}`}>
              <line
                x1={padding.left - 5}
                y1={label.y}
                x2={chartWidth - padding.right}
                y2={label.y}
                stroke="#374151"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
              <text
                x={padding.left - 10}
                y={label.y + 4}
                textAnchor="end"
                className="text-xs fill-gray-400"
              >
                {label.value}%
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {points.map((p, i) => (
            <g key={`x-label-${i}`}>
              <text
                x={p.x}
                y={chartHeight - padding.bottom + 20}
                textAnchor="middle"
                className="text-xs fill-gray-400"
              >
                {p.range}
              </text>
            </g>
          ))}

          {/* Grid background */}
          <rect
            x={padding.left}
            y={padding.top}
            width={chartWidth - padding.left - padding.right}
            height={chartHeight - padding.top - padding.bottom}
            fill="none"
            stroke="#1f2937"
            strokeWidth="1"
          />

          {/* Line path */}
          <path
            d={pathData}
            fill="none"
            stroke="#06b6d4"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points (circles) */}
          {points.map((p, i) => (
            <circle
              key={`point-${i}`}
              cx={p.x}
              cy={p.y}
              r="4"
              fill="#06b6d4"
              stroke="#1f2937"
              strokeWidth="2"
            />
          ))}

          {/* Hover targets and tooltips */}
          {points.map((p, i) => (
            <g key={`hover-${i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r="8"
                fill="transparent"
                className="hover:fill-cyan-500/10 cursor-pointer"
              />
              <title>{`${p.range}: ${p.correct}/${p.total} correct (${p.accuracy}%)`}</title>
            </g>
          ))}
        </svg>
      </div>

      {/* Legend and stats */}
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Latest Accuracy</p>
          <p className="text-lg font-semibold text-cyan-500">
            {points[points.length - 1]?.accuracy}%
          </p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Average Accuracy</p>
          <p className="text-lg font-semibold text-white">
            {(points.reduce((sum, p) => sum + p.accuracy, 0) / points.length).toFixed(1)}%
          </p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Questions Analyzed</p>
          <p className="text-lg font-semibold text-white">
            {points.reduce((sum, p) => sum + p.total, 0)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressionChart;
